import React, { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, Input } from "formik-antd";
import { useTranslation } from "react-i18next";

import { appService } from "/app/src/services";
import { handleSubmissionErrors } from "/app/src/helpers/forms";
import { App as AppType } from "/app/src/models";

interface FormValues {
  user: string | undefined;
  token: string | undefined;
}

/**
 * Formats the given app object.
 *
 * @param {AppType} app - The app object containing user and token information.
 * @returns {Object} The formatted object containing user and token.
 */
function formatForm(app: AppType) {
  const formatted = {
    user: app.user,
    token: app.token,
  };
  return formatted;
}

/**
 * GenericSettings is a React component that renders the settings form for an app.
 * @param {object} props An object containing the component props.
 * @param {AppType} props.app The application settings object.
 * @returns {JSX.Element} The rendered settings form.
 */
export default function GenericSettings({ app }: { app: AppType }) {
  const { t } = useTranslation();

  /**
   * Updates a single setting of the app.
   * @param {any} values The new form values.
   * @param {object} actions Formik actions object to handle submission errors.
   */
  const updateSettingHandler = useCallback(
    (values, actions) => {
      if (!app.id) return;
      appService.updateSingle(app.id, formatForm(values)).then((response) => {
        if ("errors" in response) {
          handleSubmissionErrors(response.errors, actions.setFieldError);
        }
      });
    },
    [app.id],
  );

  /**
   * Renders the settings form component.
   * @param {FormikProps<FormValues>} props The Formik properties, including form state.
   * @returns {JSX.Element} The rendered form component.
   */
  const settingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form layout="vertical">
          <h3>
            {app.name} - {t("translation:authentication")}
          </h3>
          <Row justify="start" gutter={16}>
            <Col span={12}>
              <Form.Item name="user" label={t("translation:user")}>
                <Input name="user" className="user" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="token" label={t("translation:token")}>
                <Input name="token" className="token" size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={24}>
              <SubmitButton type="primary" size="large" block disabled={!dirty}>
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [app.name, t],
    );

  return (
    <div className="appSettings">
      <Formik
        component={settingsForm}
        initialValues={{
          user: app.user,
          token: app.token,
        }}
        enableReinitialize
        onSubmit={updateSettingHandler}
      />
    </div>
  );
}
